/**
 * @fileoverview Reports Module
 * @description Central reporting hub. Select a report type from the dropdown
 *              to load the corresponding report. Designed to grow — new report
 *              types can be added to REPORT_TYPES without touching the shell.
 */

import React, { useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { BarChart3, ChevronDown } from 'lucide-react';
import IncidentReportTab from '@/features/IncidentAnalysis/components/IncidentReportTab';

// ── Report Registry ──────────────────────────────────────────
// To add a new report: append an entry here.  The `component`
// field is the React element/component to mount when selected.
const REPORT_TYPES = [
  {
    id: 'incident-validation',
    label: 'Incident Validation Report',
    description:
      'Batch semantic & agentic severity/priority validation for a date range of incidents.',
    badge: 'AI',
    component: IncidentReportTab,
  },
  // Future reports can be added here, e.g.:
  // {
  //   id: 'pipeline-health',
  //   label: 'Pipeline Health Report',
  //   description: 'Weekly pipeline failure trend and test pass-rate summary.',
  //   badge: null,
  //   component: PipelineHealthReport,
  // },
];

// ── Reports Page ─────────────────────────────────────────────
const Reports = () => {
  const [selectedId, setSelectedId] = useState(REPORT_TYPES[0].id);

  const selected = REPORT_TYPES.find((r) => r.id === selectedId) ?? REPORT_TYPES[0];
  const ReportComponent = selected.component;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: 'auto' }}>

      {/* ── Page Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #01426A 0%, #0097A9 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <BarChart3 size={22} color="#fff" />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#01426A" lineHeight={1.2}>
            Reports
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Select a report type to generate and export insights
          </Typography>
        </Box>
      </Box>

      {/* ── Report Selector ── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>
              REPORT TYPE
            </Typography>
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <Select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                IconComponent={ChevronDown}
                sx={{
                  fontWeight: 600,
                  '& .MuiSelect-select': { py: 1 },
                }}
              >
                {REPORT_TYPES.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{r.label}</span>
                      {r.badge && (
                        <Chip
                          label={r.badge}
                          size="small"
                          color="primary"
                          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                        />
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Description of the selected report */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
              {selected.description}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Divider sx={{ mb: 2 }} />

      {/* ── Report Content ── */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <ReportComponent />
      </Paper>
    </Box>
  );
};

export default Reports;
